<template>
  <b-card no-body>
    <b-card-header>
      <b-card-title>{{ $t('dashboard.generalStatistics') }}</b-card-title>
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        variant="primary"
        size="sm"
        :disabled="isLoading"
        @click="refreshData"
      >
        <feather-icon
          icon="RefreshCwIcon"
          :class="{ 'animate-spin': isLoading }"
        />
      </b-button>
    </b-card-header>
    <b-card-body>
      <b-row v-if="stats">
        <!-- Total Counts -->
        <b-col
          v-for="(item, key) in totalCountsItems"
          :key="`total-${key}`"
          lg="3"
          sm="6"
          class="mb-2"
        >
          <statistic-card-horizontal
            :icon="item.icon"
            :statistic="stats.total_counts[key]"
            :statistic-title="item.title"
            :color="item.color"
          />
        </b-col>

        <!-- Active Counts -->
        <b-col
          v-for="(item, key) in activeCountsItems"
          :key="`active-${key}`"
          lg="4"
          sm="6"
          class="mb-2"
        >
          <statistic-card-horizontal
            :icon="item.icon"
            :statistic="stats.active_counts[key]"
            :statistic-title="item.title"
            :color="item.color"
          />
        </b-col>

        <!-- Pending Counts -->
        <b-col
          v-for="(item, key) in pendingCountsItems"
          :key="`pending-${key}`"
          lg="6"
          sm="6"
          class="mb-2"
        >
          <statistic-card-horizontal
            :icon="item.icon"
            :statistic="stats.pending_counts[key]"
            :statistic-title="item.title"
            :color="item.color"
          />
        </b-col>
      </b-row>

      <!-- Loading State -->
      <div
        v-else-if="isLoading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>

      <!-- Empty State -->
      <div
        v-else
        class="text-center py-3"
      >
        <p class="text-muted">
          {{ $t('dashboard.noDataAvailable') }}
        </p>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BSpinner,
} from 'bootstrap-vue'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import StatisticCardHorizontal from '@core/components/statistics-cards/StatisticCardHorizontal.vue'

export default {
  name: 'GeneralStatisticsWidget',
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BSpinner,
    StatisticCardHorizontal,
  },
  directives: {
    Ripple,
  },
  computed: {
    ...mapGetters('dashboard', ['stats', 'loading']),
    isLoading() {
      return this.loading.stats
    },
    totalCountsItems() {
      return {
        projects: {
          icon: 'BriefcaseIcon',
          title: this.$t('dashboard.totalProjects'),
          color: 'primary',
        },
        customers: {
          icon: 'UsersIcon',
          title: this.$t('dashboard.totalCustomers'),
          color: 'success',
        },
        employees: {
          icon: 'UserCheckIcon',
          title: this.$t('dashboard.totalEmployees'),
          color: 'info',
        },
        transactions: {
          icon: 'DollarSignIcon',
          title: this.$t('dashboard.totalTransactions'),
          color: 'warning',
        },
      }
    },
    activeCountsItems() {
      return {
        projects: {
          icon: 'ActivityIcon',
          title: this.$t('dashboard.activeProjects'),
          color: 'success',
        },
        customers: {
          icon: 'UserPlusIcon',
          title: this.$t('dashboard.activeCustomers'),
          color: 'primary',
        },
        employees: {
          icon: 'UserIcon',
          title: this.$t('dashboard.activeEmployees'),
          color: 'info',
        },
      }
    },
    pendingCountsItems() {
      return {
        projects: {
          icon: 'ClockIcon',
          title: this.$t('dashboard.pendingProjects'),
          color: 'warning',
        },
        transactions: {
          icon: 'AlertCircleIcon',
          title: this.$t('dashboard.pendingTransactions'),
          color: 'danger',
        },
      }
    },
  },
  methods: {
    ...mapActions('dashboard', ['fetchStats', 'refreshWidget']),
    refreshData() {
      this.refreshWidget({ widget: 'stats' })
    },
  },
}
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
